import React, { useState } from "react";
import { X, UploadCloud, Loader2, Check, Link2, Image as ImageIcon, Video as VideoIcon } from "lucide-react";
import { toast } from "sonner";
import { subirMediaPrograma, setMediaPrograma } from "../lib/programasSoftware";
import { fetchProductosAdmin, subirArchivoSoftware, actualizarProducto } from "../lib/software";

// Modal admin para gestionar media de una tarjeta (video o imagen).
// tipo: "programa" (programas_software) | "producto" (software / Herramientas Técnicas).
// modo: "video" | "imagen".
export function MediaAdminModal({ open, onClose, tipo, modo, item, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [url, setUrl] = useState(item?.video_demo_url || "");

  if (!open) return null;
  const esVideo = modo === "video";
  const acceptFile = esVideo ? "video/*,.mp4,.mov,.avi,.mkv,.webm" : "image/*";

  async function persistProducto(mediaPatch) {
    // El item público carece de campos admin obligatorios; traer el producto completo y hacer PUT íntegro.
    const todos = await fetchProductosAdmin();
    const full = todos.find((p) => p.id === item.id);
    if (!full) throw new Error("Producto no encontrado");
    const payload = {
      nombre: full.nombre, descripcion: full.descripcion || "", categoria: full.categoria || "",
      tipo_producto: full.tipo_producto || "recurso", precio: Number(full.precio ?? 0), moneda: full.moneda || "PEN",
      version: full.version || null, imagen_path: full.imagen_path || null,
      archivo_path: full.archivo_path, archivo_nombre: full.archivo_nombre,
      activo: full.activo !== false, destacado: !!full.destacado,
      video_demo_path: full.video_demo_path || null, video_demo_nombre: full.video_demo_nombre || null,
      video_demo_url: full.video_demo_url || null,
      licencia_path: full.licencia_path || null, licencia_nombre: full.licencia_nombre || null,
      ...mediaPatch,
    };
    await actualizarProducto(item.id, payload);
  }

  async function onFile(file) {
    if (!file) return;
    setBusy(true); setProgreso(0);
    try {
      if (tipo === "programa") {
        await subirMediaPrograma(item.id, esVideo ? "video" : "imagen", file, setProgreso);
      } else {
        const res = await subirArchivoSoftware(file, setProgreso);
        await persistProducto(esVideo
          ? { video_demo_path: res.archivo_path, video_demo_nombre: res.archivo_nombre }
          : { imagen_path: res.archivo_path });
      }
      toast.success(esVideo ? "Video actualizado." : "Imagen actualizada.");
      onSaved?.(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo subir el archivo.");
    } finally { setBusy(false); }
  }

  async function guardarUrl() {
    setBusy(true);
    try {
      if (tipo === "programa") {
        await setMediaPrograma(item.id, { video_demo_url: url || null });
      } else {
        await persistProducto({ video_demo_url: url || null });
      }
      toast.success("Enlace de video guardado.");
      onSaved?.(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar el enlace.");
    } finally { setBusy(false); }
  }

  const Icono = esVideo ? VideoIcon : ImageIcon;
  return (
    <div className="fixed inset-0 z-[65] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="media-admin-modal" onClick={onClose}>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lifted overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
          <span className="inline-flex items-center gap-2 font-bold text-slate-900"><Icono size={18} /> {esVideo ? "Video del producto" : "Imagen del producto"}</span>
          <button onClick={onClose} data-testid="media-admin-close" className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Subir archivo</p>
            <input id="media-file" type="file" accept={acceptFile} className="hidden" data-testid="media-admin-file"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
            <label htmlFor="media-file" className="flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-500 cursor-pointer hover:border-brand-blue hover:text-brand-blue transition-colors">
              {busy ? <><Loader2 size={15} className="animate-spin" /> Subiendo {progreso}%</> : <><UploadCloud size={15} /> Seleccionar {esVideo ? "video" : "imagen"}</>}
            </label>
            <p className="mt-1 text-[11px] text-slate-400">{esVideo ? "MP4, MOV, AVI, MKV, WEBM · Máx. 200 MB." : "JPG, PNG o WEBP."}</p>
          </div>

          {esVideo && (
            <div className="pt-3 border-t border-slate-100">
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">O enlace YouTube / Vimeo</p>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Link2 size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="media-admin-url"
                    placeholder="https://www.youtube.com/watch?v=…" className="w-full pl-8 pr-3 py-2.5 rounded-lg border border-slate-300 text-sm outline-none focus:border-brand-blue" />
                </div>
                <button onClick={guardarUrl} disabled={busy} data-testid="media-admin-url-save"
                  className="inline-flex items-center gap-1 rounded-lg bg-brand-navy hover:bg-brand-blue text-white text-sm font-semibold px-3 py-2.5 disabled:opacity-60">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Guardar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MediaAdminModal;
